import CameraIcon from '@/common/assets/icons/cameraIcon.svg?react';
import CloseIcon from '@/common/assets/icons/closeIconborderLess.svg?react';
import FlashIcon from '@/common/assets/icons/flashIcon.svg?react';
import FlashIsOnIcon from '@/common/assets/icons/flashIconIsOn.svg?react';
import MuteIcon from '@/common/assets/icons/muteIcon.svg?react';
import NoMuteIcon from '@/common/assets/icons/noMuteIcon.svg?react';
import Button from '@/common/components/base/button/Button';
import Typography from '@/common/components/base/typography/Typography';
import CameraCustomMedia from '@/common/components/camera/components/CameraCustomMedia';
import CircularTimer from '@/common/components/camera/components/CircularTimer';
import ImagesSlider from '@/common/components/camera/components/ImageSlider/ImagesSlider';
import CameraDenyModal from '@/common/components/camera/components/modals/cameraDenyModal/CameraDenyModal';
import RotateModal from '@/common/components/camera/components/modals/cameraRotateModal/RotateModal';
import StopCapturingModal from '@/common/components/camera/components/modals/cameraRotateModal/StopCapturingModal';
import useCamera from '@/common/components/camera/hooks/useCamera';
import useCircularTimer from '@/common/components/camera/hooks/useCircularTimer';
import useTakePhoto from '@/common/components/camera/hooks/useTakePhoto';
import useTakeVideo from '@/common/components/camera/hooks/useTakeVideo';
import { CameraPropsType } from '@/common/components/camera/types/Camera.type';
import { SliderEnum } from '@/common/components/camera/types/components.type';
import { useEffect, useRef, useState } from 'react';
import { Box, Flex } from 'styled-system/jsx';

const Camera = ({
  handleClose,
  onDenyCamera,
  captureData,
  handleCapturedImage,
  handleRecordedVideo,
  finishFlow,
  onRotate,
  vehicleType,
}: CameraPropsType) => {
  const photoRef = useRef<HTMLCanvasElement | null>(null);
  const videoRecorderRef = useRef<ReturnType<typeof takeVideo> | null>(null);
  const isCapturing = useRef(false);
  const [cancelModal, setCancelModal] = useState(false);
  const [isFlowStarted, setIsFlowStarted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const isMutedRef = useRef(false);
  const isCapturingDamagedRef = useRef(false);

  const {
    videoRef,
    handleClickLight,
    torchIsOn,
    stream: { current: stream },
    disableCamera,
    setDisableCamera,
    stopStream,
    rotateModal,
    handleRotateModal,
  } = useCamera({
    audio: true,
    onDenyCamera: onDenyCamera,
    closeCamera: handleClose,
  });

  const {
    takePhoto,
    currentCapturingImageNumber,
    updateCurrentCapturingImage,
    resetCurrentCapturingImageNumber,
  } = useTakePhoto({
    photoRef,
    videoRef,
    rotate: true,
    onDenyCamera: onDenyCamera,
  });

  const { takeVideo } = useTakeVideo({ stream });

  const setIsCapturingDamaged = (value: boolean) => {
    isCapturingDamagedRef.current = value;
  };
  const playVoice = (voiceUrl: string | undefined) => {
    if (isMutedRef.current || !voiceUrl) return;

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    const audio = new Audio(voiceUrl);
    audioRef.current = audio;

    audio.play();
  };

  const createData = () => {
    const isCapturingDamaged = isCapturingDamagedRef.current;

    return isCapturingDamaged
      ? {
          fa_name:
            captureData.damaged_parts[
              currentCapturingImageNumber.current.currentNumber
            ]?.fa_name,
          id: captureData.damaged_parts[
            currentCapturingImageNumber.current.currentNumber
          ]?.id!,
          type: SliderEnum.DAMAGED_PARTS,
          en_name:
            captureData.damaged_parts[
              currentCapturingImageNumber.current.currentNumber
            ]?.en_name ?? '',
        }
      : {
          fa_name:
            captureData.vehicle[
              currentCapturingImageNumber.current.currentNumber
            ]?.fa_name,
          id: captureData.vehicle[
            currentCapturingImageNumber.current.currentNumber
          ]?.id!,
          type: SliderEnum.VEHICLE,
          en_name:
            captureData.vehicle[
              currentCapturingImageNumber.current.currentNumber
            ]?.en_name ?? '',
        };
  };
  const startFlow = () => {
    if (
      currentCapturingImageNumber.current.currentNumber === 0 &&
      !videoRecorderRef.current
    ) {
      videoRecorderRef.current = takeVideo();
      videoRecorderRef.current?.startVideo();
    }

    if (
      currentCapturingImageNumber.current.currentNumber === 0 &&
      !isFlowStarted
    ) {
      setDisableCamera(true);
      setIsFlowStarted(true);

      takePhoto({
        afterTakePhoto: (data) => {
          setDisableCamera(false);
          const readyData = createData();
          handleCapturedImage?.({
            ...data,
            ...readyData,
          });
          startTimer();
          playVoice(
            captureData.vehicle[
              currentCapturingImageNumber.current.currentNumber + 1
            ].voice,
          );

          updateCurrentCapturingImage();
        },
      });
    } else {
      captureImageOnShot();
    }
  };

  const checkFlowIsFinished = () => {
    const isCapturingDamaged = isCapturingDamagedRef.current;

    updateCurrentCapturingImage();
    if (
      currentCapturingImageNumber.current.totalNumber ===
      captureData.vehicle?.length + captureData.damaged_parts?.length
    ) {
      return stopAndFinishFlow();
    }

    if (
      !isCapturingDamaged &&
      currentCapturingImageNumber.current.currentNumber ===
        captureData.vehicle?.length &&
      captureData.damaged_parts.length
    ) {
      resetCurrentCapturingImageNumber();

      setIsCapturingDamaged(true);
      handleTimer();
    } else {
      handleTimer();
      if (!isCapturingDamaged) {
        playVoice(
          captureData.vehicle[currentCapturingImageNumber.current.currentNumber]
            .voice,
        );
      }
    }
  };
  const stopAndFinishFlow = () => {
    videoRecorderRef?.current?.stopVideo().then((data) => {
      handleRecordedVideo?.({
        url: data.url,
        blob: data.blob,
      });
    });
    stopStream();
    return finishFlow?.();
  };
  const handleTimer = () => {
    resetTimer();
    startTimer();
  };

  const captureImageOnShot = () => {
    setDisableCamera(true);
    takePhoto({
      afterTakePhoto: (data) => {
        setDisableCamera(false);
        const readyData = createData();
        handleCapturedImage?.({
          ...data,
          ...readyData,
        });

        checkFlowIsFinished();
      },
    });
  };
  const captureImageOnTimeEnd = () => {
    setDisableCamera(true);

    if (isCapturing.current) return;

    isCapturing.current = true;

    takePhoto({
      afterTakePhoto: (data) => {
        isCapturing.current = false;
        setDisableCamera(false);
        const readyData = createData();

        handleCapturedImage?.({
          ...data,
          ...readyData,
        });

        checkFlowIsFinished();
      },
    });
  };
  const { fraction, time, startTimer, resetTimer, timerRef } = useCircularTimer(
    {
      totalTime: 40,
      onTimerEnd: captureImageOnTimeEnd,
    },
  );
  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);
  return (
    <>
      <Flex flexDirection="column">
        <Flex
          position="relative"
          width="100%"
          backgroundColor="black-1"
          height={'calc(100dvh - 80px)'}
        >
          <ImagesSlider
            vehicleType={vehicleType}
            sliderData={captureData || []}
            turn={currentCapturingImageNumber.current.currentNumber}
            isVehicle={!isCapturingDamagedRef.current}
          />
          <CameraCustomMedia
            photoRef={photoRef}
            videoRef={videoRef}
            cameraShot={disableCamera}
          />

          {timerRef.current ? (
            <CircularTimer fraction={fraction} time={time} />
          ) : null}

          {captureData?.vehicle?.length ? (
            <Box
              position="absolute"
              display="inline-block"
              transform="rotate(90deg)"
              padding="0.5rem"
              minW="300px"
              textAlign="center"
              left="-100px"
              top="50%"
              borderRadius="8px"
              backgroundColor="white"
            >
              <Typography color="black-1">
                {isCapturingDamagedRef.current
                  ? captureData.damaged_parts?.[
                      currentCapturingImageNumber.current.currentNumber
                    ]?.fa_name
                  : captureData.vehicle?.[
                      currentCapturingImageNumber.current.currentNumber
                    ]?.description}
              </Typography>
            </Box>
          ) : null}

          {cancelModal ? (
            <CameraDenyModal
              cancel={() => {
                setCancelModal(false);
              }}
              confirm={handleClose}
            />
          ) : null}
        </Flex>

        <Flex
          backgroundColor="black-4"
          height="10%"
          width="100%"
          margin="auto"
          maxWidth="maxContainer"
          minH="100px"
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          borderRadius="24px 24px 0 0"
          alignItems="center"
          justifyContent="space-between"
          pr="2rem"
        >
          <Box
            flex={6}
            onClick={() => {
              setCancelModal(true);
            }}
          >
            <CloseIcon />
          </Box>

          <Button
            flex={5}
            backgroundColor="transparent"
            variant="text"
            p={0}
            onClick={() => (disableCamera ? undefined : startFlow())}
          >
            <CameraIcon fill={disableCamera ? '#c2c2c2' : '#fff'} />
          </Button>

          <Box flex={4} onClick={() => handleClickLight()}>
            {torchIsOn ? (
              <FlashIsOnIcon width="30px" />
            ) : (
              <FlashIcon width="30px" />
            )}
          </Box>
          <Button
            flex={4}
            backgroundColor="transparent"
            variant="text"
            p={0}
            onClick={() => {
              setIsMuted((prev) => {
                const next = !prev;

                isMutedRef.current = next;

                if (next && audioRef.current) {
                  audioRef.current.pause();
                  audioRef.current.currentTime = 0;
                }

                return next;
              });
            }}
          >
            <Box id={`mute${isMuted}`}>
              {isMuted ? (
                <MuteIcon width="30px" />
              ) : (
                <NoMuteIcon width="30px" />
              )}
            </Box>
          </Button>
        </Flex>
      </Flex>
      <RotateModal
        loading={!captureData?.vehicle?.length}
        open={rotateModal.open}
        onClose={() => {
          rotateModal.closeModal();
          playVoice(captureData.vehicle?.[0]?.voice);
        }}
      />
      {handleRotateModal.open && (
        <StopCapturingModal open={handleRotateModal.open} onClose={onRotate} />
      )}{' '}
    </>
  );
};
export default Camera;
